// Cached reference-data hooks.
//
// These replace the hand-rolled `useEffect` + `useState` + `.catch(() => [])`
// pattern that was duplicated across dozens of components. Import from
// '@platform/query' rather than reaching into this file directly.
//
// FAIL-OPEN CONTRACT: the call sites these replace all did
// `api.getFields().catch(() => [])` — a reference lookup failing must degrade
// the UI (empty dropdown), never blank-screen it. Every list hook here
// preserves that by defaulting `data` to a stable empty array and never
// throwing. Check `isError` if a caller wants to say something about it.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import coreApi, { userScopeParams } from '@platform/http/api-core';
import { qk } from './queryKeys';
import { STALE } from './queryClient';
import { EMPTY, unwrapList } from './policy';

/* ------------------------------------------------------------------ *
 * Schema-level metadata (STALE.REFERENCE)
 * ------------------------------------------------------------------ */

export function useFields(siteUrl = null, options = {}) {
  const q = useQuery({
    queryKey: qk.fields(siteUrl),
    queryFn: () => coreApi.getFields(siteUrl ?? undefined),
    staleTime: STALE.REFERENCE,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

export function useStatuses(siteUrl = null, options = {}) {
  const q = useQuery({
    queryKey: qk.statuses(siteUrl),
    queryFn: () => coreApi.getStatuses(siteUrl ?? undefined),
    staleTime: STALE.REFERENCE,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

/**
 * Link types. NOTE the envelope: /api/issue-link-types responds
 * `{ issueLinkTypes: [...] }` (metadata-handlers.js:553), NOT a bare array.
 * We unwrap here so every consumer gets a plain array and nobody has to
 * remember the shape — and so an array-coercing guard can't silently blank it.
 */
export function useIssueLinkTypes(siteUrl = null, options = {}) {
  const q = useQuery({
    queryKey: qk.issueLinkTypes(siteUrl),
    queryFn: () => coreApi.getIssueLinkTypes(siteUrl ?? undefined),
    staleTime: STALE.REFERENCE,
    ...options,
  });
  return { ...q, data: unwrapList(q.data, 'issueLinkTypes') };
}

export function usePriorities(options = {}) {
  const q = useQuery({
    queryKey: qk.priorities(),
    queryFn: () => coreApi.getPriorities(),
    staleTime: STALE.REFERENCE,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

export function useIssueTypes(options = {}) {
  const q = useQuery({
    queryKey: qk.issueTypes(),
    queryFn: () => coreApi.getIssueTypes(),
    staleTime: STALE.REFERENCE,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

/* ------------------------------------------------------------------ *
 * Directory data (STALE.DIRECTORY)
 * ------------------------------------------------------------------ */

export function useProjects(options = {}) {
  const q = useQuery({
    queryKey: qk.projects(),
    queryFn: () => coreApi.getProjects(),
    staleTime: STALE.DIRECTORY,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

/**
 * User directory. `query` '' is the full list (the common mount case) and is
 * cached under its own key, so the 13 call sites that wanted "all users" now
 * share one fetch per staleTime window instead of one fetch each.
 */
export function useUsers(query = '', options = {}) {
  const q = useQuery({
    queryKey: qk.users(query),
    queryFn: () => coreApi.getUsers(query),
    staleTime: query ? STALE.SEARCH : STALE.DIRECTORY,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

/**
 * Groups. Also enveloped: /api/groups responds
 * `{ header, total, groups: [...] }` (metadata-handlers.js:178). Unwrapped to
 * the array; `raw` keeps the envelope for the one or two callers that show the
 * header/total text.
 */
export function useGroups(query = '', options = {}) {
  const q = useQuery({
    queryKey: qk.groups(query),
    queryFn: () => coreApi.getGroups(query),
    staleTime: query ? STALE.SEARCH : STALE.DIRECTORY,
    ...options,
  });
  return { ...q, data: unwrapList(q.data, 'groups'), raw: q.data ?? null };
}

/* ------------------------------------------------------------------ *
 * Imperative search — for callback props, not render-time subscriptions
 * ------------------------------------------------------------------ */

/**
 * Returns a stable `(query) => Promise<user[]>` suitable for the
 * `onSearchUsers` callback prop that several editors take.
 *
 * Why not useUsers(): those call sites hand a function to a child component
 * which calls it on keystroke — there is no render-time subscription to hang a
 * hook off. fetchQuery still routes through the cache, so repeat terms
 * (backspacing, re-opening a picker) resolve from memory and identical
 * in-flight requests are deduped.
 *
 * NOTE: this does not by itself fix out-of-order responses in the CONSUMER —
 * a component that does `setState` on whatever resolves last can still show
 * results for a superseded query. Converting those consumers to useQuery is
 * what fixes that; this helper only removes the duplicate network traffic.
 */
export function useUserSearch() {
  const queryClient = useQueryClient();
  return useCallback((query = '', scope = null) => queryClient.fetchQuery({
    queryKey: qk.users(query, scope && scope.fieldId ? userScopeParams(scope) : null),
    queryFn: () => coreApi.getUsers(query, scope),
    staleTime: query ? STALE.SEARCH : STALE.DIRECTORY,
  }).catch(() => EMPTY), [queryClient]);
}

/**
 * Same contract as useUserSearch, for the @-mention list in rich-text
 * editors: only ACTIVE staff accounts (server-side rule,
 * GET /api/users/mentionable). Never use it for a user picker.
 */
export function useMentionSearch() {
  const queryClient = useQueryClient();
  return useCallback((query = '') => queryClient.fetchQuery({
    queryKey: qk.mentionableUsers(query),
    queryFn: () => coreApi.getMentionableUsers(query),
    staleTime: STALE.SEARCH,
  }).catch(() => EMPTY), [queryClient]);
}

/**
 * The people to suggest under a work item's comment box — ranked by how
 * involved they are with THIS item (GET /api/users/mentionable/suggestions).
 * Fail-open: an empty row, never an error.
 */
export function useMentionSuggestions(issueKey, options = {}) {
  const q = useQuery({
    queryKey: qk.mentionSuggestions(issueKey),
    queryFn: () => coreApi.getMentionSuggestions(issueKey),
    enabled: !!issueKey,
    staleTime: STALE.SEARCH,
    ...options,
  });
  return { ...q, data: unwrapList(q.data) };
}

/**
 * Same contract as useUserSearch, for group pickers. Resolves to a plain
 * ARRAY — the /api/groups envelope is unwrapped here so callers match
 * useGroups() rather than having to reach into `.groups` themselves.
 */
export function useGroupSearch() {
  const queryClient = useQueryClient();
  return useCallback((query = '') => queryClient.fetchQuery({
    queryKey: qk.groups(query),
    queryFn: () => coreApi.getGroups(query),
    staleTime: query ? STALE.SEARCH : STALE.DIRECTORY,
  }).then((d) => unwrapList(d, 'groups')).catch(() => EMPTY), [queryClient]);
}
